"use client";

/**
 * A single whiteboard surface that lives in the transcript flow.
 *
 * Focus mode changes only the surface's layout. The WhiteboardCard stays
 * mounted, so local exercise progress survives expanding and returning to chat.
 */
import type { WhiteboardRenderState } from "@/lib/whiteboard/boardState";
import type { WhiteboardSpec } from "@/lib/whiteboard/spec";
import { motion } from "framer-motion";
import { Maximize2, Minimize2, Minus, PenLine, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { LessonButton } from "./LessonPrimitives";
import { WhiteboardCard } from "./WhiteboardCard";
import {
  WHITEBOARD_FOCUSED_FRAME_CLASS,
  WHITEBOARD_INLINE_FRAME_CLASS,
  WhiteboardPhotoFrame,
} from "./WhiteboardPhotoFrame";
import { InstantWhiteboardContext } from "./WhiteboardWriteInText";
import type { WhiteboardCardEventHandler } from "./types";

interface WhiteboardConversationSurfaceProps {
  spec: WhiteboardSpec | null;
  renderState: WhiteboardRenderState;
  onEvent: WhiteboardCardEventHandler;
  focused: boolean;
  tutorRevision?: number;
  focusOnTutorRevision?: boolean | null;
  minimizedByAgent?: boolean;
  onFocusedChange: (focused: boolean) => void;
  onRestore?: () => void;
  onPresentationChange?: (exerciseId: string, visible: boolean) => void;
  onDismiss?: () => void;
  ariaLabel?: string;
  minimizedTitle?: string;
  minimizedDescription?: string;
  presentation?: "conversation" | "workspace";
}

export const WhiteboardConversationSurface: React.FC<WhiteboardConversationSurfaceProps> = ({
  spec,
  renderState,
  onEvent,
  focused,
  tutorRevision = 0,
  focusOnTutorRevision = null,
  minimizedByAgent = false,
  onFocusedChange,
  onRestore,
  onPresentationChange,
  onDismiss,
  ariaLabel = "Interactive tutor whiteboard",
  minimizedTitle,
  minimizedDescription,
  presentation = "conversation",
}) => {
  const [minimizedExerciseId, setMinimizedExerciseId] = useState<string | null>(null);
  const handledTutorRevisionRef = React.useRef(0);
  const handledSpecIdRef = React.useRef<string | null>(null);
  const minimizedByLearner = Boolean(spec && minimizedExerciseId === spec.id);
  const isMinimized = minimizedByAgent || minimizedByLearner;
  const presentationRef = React.useRef<HTMLDivElement | null>(null);

  const presentationId = spec?.id;
  useEffect(() => {
    if (!presentationId || !onPresentationChange) return;
    const node = presentationRef.current;
    const rect = node?.getBoundingClientRect();
    let inViewport = Boolean(
      rect &&
      rect.height > 0 &&
      rect.width > 0 &&
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < window.innerHeight &&
      rect.left < window.innerWidth
    );
    const report = () =>
      onPresentationChange(
        presentationId,
        !isMinimized && inViewport && document.visibilityState !== "hidden"
      );
    report();
    const observer =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(([entry]) => {
            inViewport = entry?.isIntersecting ?? false;
            report();
          })
        : null;
    if (node) observer?.observe(node);
    document.addEventListener("visibilitychange", report);
    return () => {
      observer?.disconnect();
      document.removeEventListener("visibilitychange", report);
      onPresentationChange(presentationId, false);
    };
  }, [presentationId, isMinimized, onPresentationChange]);

  useEffect(() => {
    if (!spec) {
      handledTutorRevisionRef.current = 0;
      handledSpecIdRef.current = null;
      return;
    }
    if (
      focusOnTutorRevision === null ||
      tutorRevision <= 0 ||
      tutorRevision === handledTutorRevisionRef.current
    ) {
      return;
    }
    const isNewBoard = handledSpecIdRef.current !== spec.id;
    handledTutorRevisionRef.current = tutorRevision;
    handledSpecIdRef.current = spec.id;
    setMinimizedExerciseId(null);
    if (focusOnTutorRevision || isNewBoard || minimizedByLearner) {
      onFocusedChange(focusOnTutorRevision);
    }
  }, [focusOnTutorRevision, minimizedByLearner, onFocusedChange, spec, tutorRevision]);

  useEffect(() => {
    if (isMinimized && focused) {
      onFocusedChange(false);
    }
  }, [focused, isMinimized, onFocusedChange]);

  if (!spec) return null;

  const minimizeWhiteboard = () => {
    setMinimizedExerciseId(spec.id);
    onFocusedChange(false);
  };

  const restoreWhiteboard = () => {
    setMinimizedExerciseId(null);
    onRestore?.();
  };

  const minimizedSurface = isMinimized ? (
    <motion.div
      key={`${spec.id}-minimized`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="my-5"
    >
      <button
        type="button"
        onClick={restoreWhiteboard}
        className="flex w-full items-center gap-3 rounded-xl border border-[#FD954F]/25 bg-[#FD954F]/[0.07] px-4 py-3 text-left text-sm text-[#F9F9F9] transition-colors hover:border-[#FD954F]/45 hover:bg-[#FD954F]/[0.11] focus:outline-none focus:ring-2 focus:ring-[#FD954F]/55"
        aria-label="Restore whiteboard in chat"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#FD954F]/15 text-[#FD954F]">
          <PenLine size={18} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold">
            {minimizedTitle ??
              (spec.kind === "reading_story" ? "Reading story" : "Tutor whiteboard")}
          </span>
          <span className="block text-xs text-[#AEAEB2]">
            {minimizedDescription ??
              (spec.kind === "reading_story"
                ? "Open the story again"
                : "Open the current exercise again")}
          </span>
        </span>
        <Maximize2 size={16} className="shrink-0 text-[#FD954F]" />
      </button>
    </motion.div>
  ) : null;

  const layoutClass =
    presentation === "workspace"
      ? "relative h-full min-h-0 w-full"
      : focused
        ? "fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-[#0f0f14]/95 px-3 py-16 backdrop-blur-sm sm:px-8 sm:py-20"
        : "relative my-5 scroll-mt-24";
  const shellClass =
    presentation === "workspace"
      ? "h-full min-h-0 w-full rounded-none shadow-none"
      : focused
        ? WHITEBOARD_FOCUSED_FRAME_CLASS
        : WHITEBOARD_INLINE_FRAME_CLASS;
  const studySurface = Boolean(
    ("study" in spec && spec.study) || ("generated" in spec && spec.generated)
  );
  const plainLanguageSurface =
    presentation === "conversation" && spec.kind !== "equation_balance" && !studySurface;
  const darkSurface = studySurface || plainLanguageSurface;
  const controlClass = darkSurface
    ? "rounded-full border border-white/10 bg-[#1B1C22] p-1.5 text-[#AEAEB2] transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#FD954F]/50"
    : "rounded-full border border-slate-900/20 bg-white/80 p-1.5 text-slate-800 shadow-sm transition-colors hover:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/35";

  return (
    <>
      {minimizedSurface}
      <motion.div
        key={spec.id}
        ref={presentationRef}
        layout
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 30 }}
        className={isMinimized ? "hidden" : layoutClass}
        hidden={isMinimized}
        data-testid="conversation-whiteboard-surface"
      >
        <WhiteboardPhotoFrame
          paginated={!!("generated" in spec && spec.generated)}
          appearance={studySurface ? "study" : plainLanguageSurface ? "plain" : "photo"}
          className={
            studySurface
              ? presentation === "workspace"
                ? "h-full min-h-0 w-full"
                : "h-[min(78dvh,48rem)] w-full max-w-3xl"
              : plainLanguageSurface
                ? "w-full max-w-3xl max-h-[min(78dvh,48rem)]"
                : shellClass
          }
          role="region"
          aria-label={ariaLabel}
          controls={
            presentation === "workspace" ? undefined : (
              <>
                {onDismiss ? (
                  <LessonButton
                    intent="chrome"
                    type="button"
                    onClick={onDismiss}
                    className={controlClass}
                    aria-label="Close exercise"
                    title="Close exercise"
                  >
                    <X size={16} />
                  </LessonButton>
                ) : null}
                <LessonButton
                  intent="chrome"
                  type="button"
                  onClick={minimizeWhiteboard}
                  className={controlClass}
                  aria-label="Minimize whiteboard"
                  title="Minimize whiteboard"
                >
                  <Minus size={16} />
                </LessonButton>
                <LessonButton
                  intent="chrome"
                  type="button"
                  onClick={() => onFocusedChange(!focused)}
                  className={controlClass}
                  aria-label={focused ? "Return whiteboard to chat" : "Expand whiteboard"}
                  title={focused ? "Return whiteboard to chat" : "Expand whiteboard"}
                >
                  {focused ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </LessonButton>
              </>
            )
          }
        >
          <InstantWhiteboardContext.Provider value={plainLanguageSurface}>
            <WhiteboardCard
              spec={spec}
              renderState={renderState}
              onEvent={onEvent}
              variant={darkSurface ? "dark" : "photo"}
              embedded={plainLanguageSurface}
            />
          </InstantWhiteboardContext.Provider>
        </WhiteboardPhotoFrame>
      </motion.div>
    </>
  );
};
